/**
 * Firmar con el dedo, y que la firma valga como evidencia.
 *
 * La entrega de un carro es un acto entre dos personas y hasta ahora solo la
 * registraba una: quien tenía el teléfono tomaba diecisiete fotos, anotaba el
 * odómetro, y la otra parte nunca decía que estaba de acuerdo. Las columnas
 * `guest_signature_url` y `host_signature_url` existen en `rental_inspections`
 * desde el principio y ningún código las escribía — cero filas con firma.
 *
 * SE GUARDA COMO SVG, NO COMO IMAGEN. Un trazo es una lista de puntos: en SVG
 * pesa unos pocos kilobytes, se ve nítido a cualquier tamaño y se puede volver a
 * dibujar tal cual dentro de un PDF años después. Rasterizarlo sería tirar
 * información para ocupar más espacio.
 *
 * DOS COSAS QUE NO SE PUEDEN OMITIR, y por eso están acá y no en la pantalla:
 * el trazo tiene que tener sustancia (un punto accidental no es una firma), y
 * tiene que poder borrarse. Nadie firma bien a la primera con el dedo.
 *
 * Y UNA TERCERA, QUE FALTABA: el pad tiene que poder MOSTRAR una firma que ya
 * existe. El trazo vivía solo en el `useState` interno y el único prop de datos
 * era `onChange`, así que cuando la pantalla recuperaba un borrador del disco
 * pasaba lo peor: la tarjeta de completitud decía "Firmas ✓" y el botón quedaba
 * habilitado, mientras los dos recuadros se veían EN BLANCO. Quien creía que le
 * faltaba firmar tocaba el recuadro, hacía un trazo corto, y `emit` disparaba
 * `onChange(null)` — borrando la firma que sí estaba guardada. Una pantalla que
 * afirma algo que no muestra y que destruye lo que afirmaba.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { runOnJS } from 'react-native-reanimated'
import Svg, { Path } from 'react-native-svg'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'

/**
 * Un trazo tan corto es un roce, no una firma. Medido en píxeles recorridos,
 * no en número de puntos: un toque largo genera decenas de puntos en el mismo
 * sitio y no dibuja nada.
 */
const MIN_STROKE_LENGTH = 120

const PAD_HEIGHT = 200

interface SignaturePadProps {
  /** Quién firma. Se muestra bajo la línea, como en un papel. */
  label: string
  /**
   * La firma que ya existe, como SVG — típicamente recuperada de un borrador.
   * Se dibuja dentro del recuadro en cuanto llega, una sola vez: a partir de
   * ahí manda lo que la persona haga con el dedo.
   */
  value?: string | null
  onChange: (svg: string | null) => void
  testID?: string
}

/**
 * Los trazos de vuelta desde el SVG que este mismo componente generó.
 *
 * No es un parser de SVG: es la inversa exacta de `emit`, sobre el formato que
 * escribimos nosotros. Por eso alcanza con leer los `d`.
 */
function trazosDelSvg(svg: string | null | undefined): string[] {
  if (!svg) return []
  const trazos: string[] = []
  const re = /\sd="([^"]+)"/g
  let m: RegExpExecArray | null
  while ((m = re.exec(svg)) !== null) {
    const d = m[1]
    if (d) trazos.push(d)
  }
  return trazos
}

/** El ancho con el que se dibujó una firma guardada, para no deformarla. */
function anchoDelSvg(svg: string | null | undefined): number | null {
  const m = svg ? /viewBox="0 0 (\d+(?:\.\d+)?) /.exec(svg) : null
  const n = m ? Number(m[1]) : Number.NaN
  return Number.isFinite(n) && n > 0 ? n : null
}

export function SignaturePad({
  label,
  value,
  onChange,
  testID = 'signature-pad',
}: SignaturePadProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const [paths, setPaths] = useState<string[]>([])
  const [current, setCurrent] = useState<string>('')
  /*
   * Espejo del trazo en curso.
   *
   * Existe para poder LEERLO sin meterse dentro de un actualizador de estado.
   * `endStroke` hacía `setCurrent(d => { setPaths(prev => { ...; emit(next) }) })`
   * — dos actualizadores anidados y, dentro del más profundo, `onChange`, que
   * es un `setState` del componente PADRE. React lo avisa en voz alta
   * ("Cannot update a component while rendering a different component") porque
   * un actualizador tiene que ser puro: React puede volver a ejecutarlo, y
   * entonces la firma se emite dos veces o no se emite.
   *
   * Lo vi en el Pixel al firmar con el dedo; en el navegador no aparecía.
   */
  const currentRef = useRef('')
  /** Espejo de los trazos, por el mismo motivo que `currentRef`. */
  const pathsRef = useRef<string[]>([])
  // El ancho real del pad, medido en onLayout. Va ANTES de `emit` porque el
  // viewBox lo necesita: capturado como 0 por una dependencia olvidada, el SVG
  // sale con `viewBox="0 0 0 200"` y la firma se renderiza vacía en todos lados
  // menos en el teléfono donde se hizo.
  const [width, setWidth] = useState(0)
  const lengthRef = useRef(0)
  const lastRef = useRef<{ x: number; y: number } | null>(null)
  /*
   * Una firma restaurada se dibuja UNA sola vez. Después manda el dedo: si se
   * volviera a sembrar en cada render, borrar la firma para rehacerla sería
   * imposible — el prop la repondría enseguida.
   */
  const yaSembrado = useRef(false)
  /*
   * El ancho original de una firma restaurada. Si se le agrega un trazo, el SVG
   * nuevo se emite en ESE mismo sistema de coordenadas: mezclar dos anchos
   * distintos en un mismo viewBox deforma lo que ya estaba dibujado.
   */
  const anchoSembrado = useRef<number | null>(null)

  useEffect(() => {
    if (yaSembrado.current) return
    const trazos = trazosDelSvg(value)
    if (trazos.length === 0) return
    yaSembrado.current = true
    anchoSembrado.current = anchoDelSvg(value)
    pathsRef.current = trazos
    setPaths(trazos)
    // Una firma que ya se guardó pasó este umbral cuando se hizo. Sin esto, el
    // primer trazo nuevo la evaluaría desde cero y la anularía entera.
    lengthRef.current = MIN_STROKE_LENGTH
  }, [value])

  const emit = useCallback(
    (allPaths: string[]) => {
      if (allPaths.length === 0 || lengthRef.current < MIN_STROKE_LENGTH) {
        onChange(null)
        return
      }
      /*
       * El viewBox lleva las dimensiones reales del pad, así que la firma se
       * puede volver a dibujar con la misma proporción en cualquier lado — un
       * PDF, la web, un correo — sin saber en qué teléfono se hizo.
       *
       * `vector-effect="non-scaling-stroke"` mantiene el grosor del trazo al
       * escalar. Sin eso una firma ampliada a tamaño carta se convierte en una
       * mancha.
       */
      const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${Math.round(
          anchoSembrado.current ?? width,
        )} ${PAD_HEIGHT}">` +
        allPaths
          .map(
            (d) =>
              `<path d="${d}" fill="none" stroke="#111111" stroke-width="2.5" ` +
              `stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>`,
          )
          .join('') +
        `</svg>`
      onChange(svg)
    },
    [onChange, width],
  )

  const appendPoint = useCallback((x: number, y: number, isStart: boolean) => {
    // Desde el primer contacto manda el dedo, no el prop.
    yaSembrado.current = true
    if (isStart) {
      lastRef.current = { x, y }
      currentRef.current = `M ${x.toFixed(1)} ${y.toFixed(1)}`
      setCurrent(currentRef.current)
      return
    }
    const last = lastRef.current
    if (last) {
      lengthRef.current += Math.hypot(x - last.x, y - last.y)
    }
    lastRef.current = { x, y }
    currentRef.current = `${currentRef.current} L ${x.toFixed(1)} ${y.toFixed(1)}`
    setCurrent(currentRef.current)
  }, [])

  /**
   * Se levanta el dedo: el trazo pasa a la lista y se avisa al padre.
   *
   * Plano a propósito, sin actualizadores anidados y sin llamar a `onChange`
   * dentro de uno. Ver la nota de `currentRef`: la versión anterior lo hacía y
   * React avisaba, con razón, que puede ejecutar un actualizador dos veces.
   */
  const endStroke = useCallback(() => {
    const trazo = currentRef.current
    currentRef.current = ''
    lastRef.current = null
    if (!trazo) {
      setCurrent('')
      return
    }
    const siguientes = [...pathsRef.current, trazo]
    pathsRef.current = siguientes
    setCurrent('')
    setPaths(siguientes)
    emit(siguientes)
  }, [emit])

  /*
   * Gesture API, no PanResponder. Este pad vive dentro de un ScrollView: una
   * firma empieza casi siempre con movimiento horizontal, mientras un flick
   * vertical suele ser la persona buscando el siguiente campo. Antes el pad
   * reclamaba ambos desde onBegin; al intentar desplazarse sobre el recuadro se
   * podía guardar una raya vertical como firma.
   *
   * El gesto no empieza hasta que hay intención horizontal y falla rápido ante
   * un desplazamiento vertical. Así el ScrollView conserva los flicks y el pad
   * solo toma el dedo cuando realmente se comenzó a firmar.
   */
  const pan = useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetX([-8, 8])
        .failOffsetY([-24, 24])
        .onStart((e) => {
          runOnJS(appendPoint)(e.x, e.y, true)
        })
        .onUpdate((e) => {
          runOnJS(appendPoint)(e.x, e.y, false)
        })
        .onFinalize(() => {
          runOnJS(endStroke)()
        }),
    [appendPoint, endStroke],
  )

  const clear = useCallback(() => {
    // Borrar es una decisión de la persona: el prop no puede deshacerla
    // repintando la firma anterior en el render siguiente.
    yaSembrado.current = true
    anchoSembrado.current = null
    pathsRef.current = []
    currentRef.current = ''
    setPaths([])
    setCurrent('')
    lengthRef.current = 0
    lastRef.current = null
    onChange(null)
  }, [onChange])

  const hasSignature = paths.length > 0 || current.length > 0

  return (
    <View testID={testID}>
      <GestureDetector gesture={pan}>
        <View
          onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
          style={{
            height: PAD_HEIGHT,
            borderRadius: RADIUS.md,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.surface,
            overflow: 'hidden',
          }}
          accessibilityRole="none"
          accessibilityLabel={t('inspection.signature_area', { who: label })}
          testID={`${testID}.canvas`}
        >
          <Svg width="100%" height={PAD_HEIGHT}>
            {paths.map((d, i) => (
              <Path
                key={i}
                d={d}
                stroke={colors.textPrimary}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            ))}
            {current ? (
              <Path
                d={current}
                stroke={colors.textPrimary}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            ) : null}
          </Svg>

          {/* La línea y el nombre debajo, como en un papel. Sin ellos el
              recuadro vacío no dice qué se espera que pase adentro. */}
          {!hasSignature && (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: SPACING.lg,
                right: SPACING.lg,
                bottom: SPACING.lg,
              }}
            >
              <View style={{ height: 1, backgroundColor: colors.border }} />
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  marginTop: SPACING.xs,
                }}
              >
                {t('inspection.signature_hint', { who: label })}
              </Text>
            </View>
          )}
        </View>
      </GestureDetector>

      {hasSignature && (
        <View style={{ alignItems: 'flex-end', marginTop: SPACING.xs }}>
          <Button
            title={t('inspection.signature_clear')}
            variant="text"
            onPress={clear}
            testID={`${testID}.clear`}
          />
        </View>
      )}
    </View>
  )
}
